import React, { useState, useEffect, useRef } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import {
  professionalSearch,
  invitedJobsService,
  fetchBedSizeOptions,
  getSkillsList,
} from "../../../services/commonServices";
import {
  fetchProfessionType,
  fetchSpecialisations,
  fetchCityList,
  fetchDepartments,
  fetchQualificationType,
  fetchExperience,
} from "../../../services/eventServices";
import { hospitalBranch } from "../../../services/hospitalServices";
import "./Candidatesearchpage.css";
import {
  filterCandidates,
  sortCandidates,
  mockFilterOptions,
  BED_SIZE_OPTIONS,
  findSimilarOptions,
  isSimilarOption,
  resolveSearchQuery,
  SEARCHABLE_FILTER_KEYS,
  candidateMatchesJobCity,
  isNcrCity,
} from "./CandidateSearchUtils";

import {
  I,
  Avatar,
  Badge,
  StatusBadge,
  RegBadge,
  WorkedBadge,
  FilterDropdown,
} from "./CandidateSearchComponents";
import { toast } from "react-toastify";

const defaultGradients = [
  ["#FDC830", "#F37335"],
  ["#ee9ca7", "#ffdde1"],
  ["#43cea2", "#185a9d"],
  ["#c94b4b", "#4b134f"],
  ["#11998e", "#38ef7d"],
  ["#ff5858", "#f857a6"],
  ["#7F00FF", "#E100FF"],
  ["#FFAFBD", "#ffc3a0"],
  ["#f7971e", "#ffd200"],
  ["#373B44", "#4286f4"],
  ["#0cebeb", "#29ffc6"],
  ["#1A2980", "#26D0CE"],
  ["#536976", "#292E49"],
];

const escapeCsvCell = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const str = String(value);
  return /[",\n\r]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

const formatCsvPhone = (phone) => {
  if (phone === null || phone === undefined || phone === "") return "";
  const digits = String(phone).replace(/\D/g, "");
  return digits ? `="+${digits}"` : "";
};

const formatCsvDate = (dateValue) => {
  if (!dateValue) return "";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const parseSkillsFromDoc = (doc) => {
  if (Array.isArray(doc?.additionalSkill) && doc.additionalSkill.length > 0) {
    return doc.additionalSkill
      .map((skill) => (typeof skill === "string" ? skill : skill?.name))
      .filter(Boolean);
  }
  if (Array.isArray(doc?.skills) && doc.skills.length > 0) {
    return doc.skills
      .map((skill) => (typeof skill === "string" ? skill : skill?.name))
      .filter(Boolean);
  }
  if (typeof doc?.skills === "string" && doc.skills.trim()) {
    return doc.skills
      .split(",")
      .map((skill) => skill.trim())
      .filter(Boolean);
  }
  return [];
};

const parseLanguageFromDoc = (doc) => {
  if (Array.isArray(doc?.language) && doc.language.length > 0) {
    return doc.language
      .map((language) =>
        typeof language === "string" ? language : language?.name,
      )
      .filter(Boolean);
  }

  if (typeof doc?.language === "string" && doc.language.trim()) {
    return doc.language
      .split(",")
      .map((language) => language.trim())
      .filter(Boolean);
  }
  return [];
};

const normalizeSkillName = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const getSkillAliases = (skillName) => {
  const raw = String(skillName || "").trim();
  const aliases = new Set();
  if (!raw) return aliases;

  aliases.add(normalizeSkillName(raw));

  const parentheticalMatches = raw.match(/\(([^)]+)\)/g) || [];
  parentheticalMatches.forEach((segment) => {
    const inner = segment.replace(/[()]/g, "").trim();
    if (inner) aliases.add(normalizeSkillName(inner));
  });

  return aliases;
};

const shareSkillAlias = (skillA, skillB) => {
  const aliasesA = getSkillAliases(skillA);
  const aliasesB = getSkillAliases(skillB);
  for (const alias of aliasesA) {
    if (aliasesB.has(alias)) return true;
  }
  return false;
};

const expandSkillFilterValues = (selectedSkills, skillOptions = []) => {
  const expanded = new Set(selectedSkills);
  selectedSkills.forEach((selected) => {
    findSimilarOptions(selected, skillOptions).forEach((option) =>
      expanded.add(option),
    );
    skillOptions.forEach((option) => {
      if (shareSkillAlias(selected, option)) {
        expanded.add(option);
      }
    });
  });
  return Array.from(expanded);
};

const skillValuesMatch = (candidateSkill, filterSkill, mode) => {
  const candidateNorm = normalizeSkillName(candidateSkill);
  const filterNorm = normalizeSkillName(filterSkill);
  if (!candidateNorm || !filterNorm) return false;

  if (shareSkillAlias(candidateSkill, filterSkill)) return true;

  if (mode === "equals") {
    return candidateNorm === filterNorm;
  }

  return (
    candidateNorm.includes(filterNorm) || filterNorm.includes(candidateNorm)
  );
};

const candidateMatchesSkillsFilter = (candidateSkills, filterSkills, mode) => {
  if (!filterSkills?.length) return true;
  return filterSkills.some((filterSkill) =>
    (candidateSkills || []).some((candidateSkill) =>
      skillValuesMatch(candidateSkill, filterSkill, mode),
    ),
  );
};

const formatCsvLanguages = (doc) => {
  if (Array.isArray(doc.languages) && doc.languages.length > 0) {
    return doc.languages.join(" | ");
  }
  if (Array.isArray(doc.languageKnownNew) && doc.languageKnownNew.length > 0) {
    return doc.languageKnownNew.join(" | ");
  }
  if (Array.isArray(doc.languageKnown) && doc.languageKnown.length > 0) {
    return doc.languageKnown
      .map((lang) => (typeof lang === "string" ? lang : lang?.name))
      .filter(Boolean)
      .join(" | ");
  }
  if (typeof doc.languageKnown === "string" && doc.languageKnown !== "NA") {
    return doc.languageKnown;
  }
  return "";
};

const KNOWN_RESUME_EXTENSIONS = new Set([
  "pdf",
  "doc",
  "docx",
  "dot",
  "dotx",
  "xls",
  "xlsx",
  "xlsm",
  "ppt",
  "pptx",
  "pps",
  "ppsx",
  "rtf",
  "txt",
  "htm",
  "html",
  "csv",
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "bmp",
]);

const getResumeExtension = (url = "") => {
  const clean = url.split("?")[0].split("#")[0];
  const filename = (clean.split("/").pop() || "").toLowerCase();
  for (const ext of KNOWN_RESUME_EXTENSIONS) {
    if (filename.endsWith(`.${ext}`)) return ext;
  }
  const parts = filename.split(".");
  if (parts.length > 1) {
    const last = parts[parts.length - 1];
    if (last.length <= 5) return last;
  }
  return "";
};

const isImageResume = (url) =>
  ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"].includes(
    getResumeExtension(url),
  );

const OFFICE_VIEWER_EXTENSIONS = new Set([
  "doc",
  "docx",
  "dot",
  "dotx",
  "xls",
  "xlsx",
  "xlsm",
  "ppt",
  "pptx",
  "pps",
  "ppsx",
  "rtf",
]);

const DIRECT_VIEW_EXTENSIONS = new Set(["txt", "htm", "html", "csv"]);

const MIME_TO_EXTENSION = {
  "application/pdf": "pdf",
  "application/msword": "doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "docx",
  "application/vnd.ms-excel": "xls",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "application/vnd.ms-powerpoint": "ppt",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    "pptx",
  "text/plain": "txt",
  "text/html": "html",
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/gif": "gif",
  "image/webp": "webp",
};

const getOfficeViewerUrl = (url) =>
  `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(url)}`;

const IMAGE_MIME_TYPES = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  gif: "image/gif",
  webp: "image/webp",
  bmp: "image/bmp",
};

const getImageMimeType = (url, blobType = "") => {
  if (blobType?.startsWith("image/")) return blobType;
  return IMAGE_MIME_TYPES[getResumeExtension(url)] || "image/jpeg";
};

const getResumeFileName = (url = "") => {
  const clean = url.split("?")[0].split("#")[0];
  return clean.split("/").pop() || "resume";
};

const openImageViewerTab = (imageUrl, fileName = "resume") => {
  const safeUrl = imageUrl.replace(/"/g, "&quot;");
  const safeName = fileName.replace(/"/g, "&quot;");
  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${safeName}</title>
  <style>
    * { box-sizing: border-box; }
    body { margin: 0; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; background: #323639; color: #fff; }
    .toolbar { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 10px 16px; background: #1e1e1e; border-bottom: 1px solid #3c3c3c; position: sticky; top: 0; z-index: 1; }
    .title { font-size: 14px; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .download-btn { display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px; background: #4d8efd; color: #fff; border: none; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; text-decoration: none; flex-shrink: 0; }
    .download-btn:hover { background: #3b7de0; }
    .content { display: flex; justify-content: center; padding: 24px; min-height: calc(100vh - 52px); }
    img { max-width: 100%; height: auto; background: #fff; border-radius: 4px; box-shadow: 0 4px 24px rgba(0,0,0,0.35); }
  </style>
</head>
<body>
  <div class="toolbar">
    <div class="title">${safeName}</div>
    <a class="download-btn" href="${safeUrl}" download="${safeName}">Download</a>
  </div>
  <div class="content">
    <img src="${safeUrl}" alt="Resume" />
  </div>
</body>
</html>`;
  const htmlBlob = new Blob([html], { type: "text/html" });
  window.open(URL.createObjectURL(htmlBlob), "_blank", "noopener,noreferrer");
};

const fetchResumeBlob = async (url) => {
  const response = await fetch(url, { mode: "cors" });
  if (!response.ok) throw new Error("Failed to fetch resume");
  return response.blob();
};

const detectResumeType = async (url) => {
  const ext = getResumeExtension(url);
  if (ext && KNOWN_RESUME_EXTENSIONS.has(ext)) return ext;

  try {
    const response = await fetch(url, { method: "HEAD", mode: "cors" });
    const contentType = response.headers
      .get("content-type")
      ?.split(";")[0]
      ?.trim()
      ?.toLowerCase();
    if (contentType) {
      if (MIME_TO_EXTENSION[contentType]) return MIME_TO_EXTENSION[contentType];
      if (contentType.includes("pdf")) return "pdf";
      if (contentType.includes("word") || contentType.includes("document"))
        return "docx";
      if (contentType.includes("excel") || contentType.includes("sheet"))
        return "xlsx";
      if (contentType.includes("powerpoint") || contentType.includes("presentation"))
        return "pptx";
      if (contentType.startsWith("image/")) return "image";
    }
  } catch (error) {
    // HEAD may fail on CORS — fall back to extension heuristics below.
  }

  return ext || "unknown";
};

const getResumeViewUrl = async (url) => {
  const type = await detectResumeType(url);

  if (type === "pdf") {
    try {
      const blob = await fetchResumeBlob(url);
      return URL.createObjectURL(
        new Blob([blob], { type: "application/pdf" }),
      );
    } catch (error) {
      return url;
    }
  }

  if (OFFICE_VIEWER_EXTENSIONS.has(type) || type === "unknown") {
    return getOfficeViewerUrl(url);
  }

  if (DIRECT_VIEW_EXTENSIONS.has(type)) {
    return url;
  }

  return getOfficeViewerUrl(url);
};

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const openImageResume = async (url) => {
  const fileName = getResumeFileName(url);
  try {
    const blob = await fetchResumeBlob(url);
    const mimeType = getImageMimeType(url, blob.type);
    const dataUrl = await blobToDataUrl(new Blob([blob], { type: mimeType }));
    openImageViewerTab(dataUrl, fileName);
  } catch (error) {
    openImageViewerTab(url, fileName);
  }
};

const openResumeFile = async (url) => {
  if (!url) return;

   if (url.startsWith("http://")) {
     url = url.replace("http://", "https://");
   }

  try {
    const type = await detectResumeType(url);

    if (isImageResume(url) || type === "image") {
      await openImageResume(url);
      return;
    }

    const viewUrl = await getResumeViewUrl(url);
    window.open(viewUrl, "_blank", "noopener,noreferrer");
  } catch (error) {
    if (isImageResume(url)) {
      await openImageResume(url);
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  }
};

const getCertificateUrl = (certificate) => {
  if (!certificate) return "";
  if (typeof certificate === "string") return certificate;
  return (
    certificate?.SecureUrl ||
    certificate?.secureUrl ||
    certificate?.uri ||
    certificate?.url ||
    certificate?.files?.[0]?.uri ||
    certificate?.files?.[0]?.url ||
    ""
  );
};

const normalizeAuthorityName = (value) => String(value || "").trim();

const normalizeAuthorityNameForMatch = (value) =>
  normalizeAuthorityName(value).toLowerCase();

const authorityNameValuesMatch = (candidateName, filterName, mode) => {
  const candidateNorm = normalizeAuthorityNameForMatch(candidateName);
  const filterNorm = normalizeAuthorityNameForMatch(filterName);
  if (!candidateNorm || !filterNorm) return false;

  if (mode === "equals") {
    return candidateNorm === filterNorm;
  }

  return (
    candidateNorm.includes(filterNorm) ||
    filterNorm.includes(candidateNorm) ||
    isSimilarOption(candidateName, filterName)
  );
};

const candidateMatchesAuthorityNameFilter = (
  candidateName,
  filterNames,
  mode,
) => {
  if (!filterNames?.length) return true;
  // Equals uses AND across selected values (same as specialization equals):
  // every selected authority must match. For a single authority field, picking
  // two different names correctly yields no rows.
  // Contains stays OR so related names can still match.
  const compare = (filterName) =>
    authorityNameValuesMatch(candidateName, filterName, mode);
  return mode === "equals"
    ? filterNames.every(compare)
    : filterNames.some(compare);
};

const mergeAuthorityNameOptions = (prevOptions, nextNames) => {
  const seen = new Set(
    prevOptions.map((name) => name.toLowerCase()).filter(Boolean),
  );
  const merged = [...prevOptions];
  let changed = false;
  nextNames.forEach((raw) => {
    const name = normalizeAuthorityName(raw);
    if (!name) return;
    const key = name.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    merged.push(name);
    changed = true;
  });
  if (!changed) return prevOptions;
  return merged.sort((a, b) => a.localeCompare(b));
};

const normalizeProfession = (value) => (value || "").trim().toLowerCase();

const candidateMatchesJobProfession = (
  candidateProfession,
  requiredProfession,
) => {
  if (!requiredProfession) return true;
  return (
    normalizeProfession(candidateProfession) ===
    normalizeProfession(requiredProfession)
  );
};

const isCandidateInactive = (status) =>
  (status || "").toUpperCase() === "INACTIVE";

const normalizeExperienceValue = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .replace(/yrs?\b/gi, "years");

const buildContainsMatchFilter = (
  key,
  appliedFilters,
  appliedBaseFilters,
  appliedModes,
) => {
  const mode = appliedModes[key];
  const values =
    mode === "equals"
      ? appliedBaseFilters[key] || []
      : appliedFilters[key] || [];
  if (!values.length) return undefined;
  return { value: values.join(","), match: mode };
};

const getInviteStatusLabel = (doc = {}) => {
  if (doc.isAccepted === true) return "Accepted";
  if (doc.isApplied === true) return "Applied";
  if (doc.isInvited === true) return "Invited";
  return "Not Invited";
};

const canInviteCandidate = (candidate, requiredProfession, requiredCity) =>
  candidateMatchesJobProfession(candidate.profession, requiredProfession) &&
  candidateMatchesJobCity(candidate.city, requiredCity) &&
  !isCandidateInactive(candidate.status) &&
  candidate.inviteStatus !== "Invited" &&
  candidate.inviteStatus !== "Applied" &&
  candidate.inviteStatus !== "Accepted";

const cloneFilters = (source) =>
  Object.fromEntries(
    Object.entries(source).map(([key, value]) => [
      key,
      Array.isArray(value) ? [...value] : value,
    ]),
  );

const cleanFilterValue = (value) =>
  typeof value === "string" ? value.trim() : value;

const isFiltersEmpty = (f) =>
  Object.values(f).every((v) =>
    Array.isArray(v) ? v.length === 0 : !v || v === "All",
  );

const EMPTY_FILTERS = {
  city: [],
  profession: [],
  department: [],
  hospital: [],
  status: [],
  registration: [],
  specialization: [],
  qualification: [],
  skills: [],
  experience: [],
  gender: [],
  bedSize: [],
  authorityName: [],
};

const EMPTY_BASE_FILTERS = {
  department: [],
  specialization: [],
  qualification: [],
};

export default function CandidateSearchPage({ mode = "job" } = {}) {
  const isUsersMode = mode === "users";
  const location = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const incomingJob = location.state?.job || null;

  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [searchDerivedFilters, setSearchDerivedFilters] = useState(() =>
    Object.fromEntries(SEARCHABLE_FILTER_KEYS.map((key) => [key, []])),
  );
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [showFilters, setShowFilters] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isInviting, setIsInviting] = useState(false);
  const [selectedCandidateDetails, setSelectedCandidateDetails] =
    useState(null);

  // Interactive UI State
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [sortOrder, setSortOrder] = useState("Most Shifts at this hospital");
  const [apiCandidates, setApiCandidates] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(true);
  const [filtersCleared, setFiltersCleared] = useState(isUsersMode);

  const { adminData } = useSelector((state) => ({
    adminData: state?.auth?.adminData,
  }));

  const copyPhone = (e, phone) => {
    e.stopPropagation();
    if (!phone) return;
    navigator.clipboard.writeText(phone);
    toast.success("Phone number copied to clipboard.");
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
  };

  // Filters
  const [filters, setFilters] = useState(() => {
    if (isUsersMode) {
      return { ...EMPTY_FILTERS };
    }
    if (incomingJob) {
      return {
        city: incomingJob.city ? [cleanFilterValue(incomingJob.city)] : [],
        profession: incomingJob.profession
          ? [cleanFilterValue(incomingJob.profession)]
          : [],
        department: incomingJob.department
          ? [cleanFilterValue(incomingJob.department)]
          : [],
        hospital: [],
        status: [],
        registration: [],
        specialization: incomingJob.specialization
          ? [cleanFilterValue(incomingJob.specialization)]
          : [],
        qualification: incomingJob.qualification
          ? [cleanFilterValue(incomingJob.qualification)]
          : [],
        skills: [],
        experience: [],
        gender: [],
        bedSize: [],
        authorityName: [],
      };
    }
    return {
      city: ["Bengaluru"],
      profession: ["Doctor"],
      department: ["Gynaecology"],
      hospital: [],
      status: [],
      registration: [],
      specialization: [],
      qualification: [],
      skills: [],
      experience: [],
      gender: [],
      bedSize: [],
      authorityName: [],
    };
  });

  const [modes, setModes] = useState({
    hospital: "or",
    department: "contains",
    specialization: "contains",
    qualification: "contains",
    skills: "contains",
    authorityName: "contains",
  });

  // Base (canonical) selections for the contains-capable filters. The live
  // `filters` selection is DERIVED from these: in "equals" mode it equals the
  // base values; in "contains" mode it is the base values expanded via the
  // similarity engine. Expanded values are never stored here — base is the
  // single source of truth, so toggling Equals <-> Contains is reversible.
  const [baseFilters, setBaseFilters] = useState(() => {
    if (isUsersMode) {
      return { ...EMPTY_BASE_FILTERS };
    }
    return {
      department: incomingJob
        ? incomingJob.department
          ? [incomingJob.department]
          : []
        : ["Gynaecology"],
      specialization:
        incomingJob && incomingJob.specialization
          ? [incomingJob.specialization]
          : [],
      qualification:
        incomingJob && incomingJob.qualification
          ? [incomingJob.qualification]
          : [],
    };
  });

  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [appliedBaseFilters, setAppliedBaseFilters] = useState(() => ({
    department: baseFilters.department,
    specialization: baseFilters.specialization,
    qualification: baseFilters.qualification,
  }));
  const [appliedModes, setAppliedModes] = useState({
    hospital: "or",
    department: "contains",
    specialization: "contains",
    qualification: "contains",
    skills: "contains",
    authorityName: "contains",
  });
  const [appliedFiltersCleared, setAppliedFiltersCleared] =
    useState(isUsersMode);

  useEffect(() => {
    setAppliedFilters(cloneFilters(filters));
    setAppliedModes({ ...modes });
    setAppliedFiltersCleared(filtersCleared);
    setAppliedBaseFilters({
      department: [...baseFilters.department],
      specialization: [...baseFilters.specialization],
      qualification: [...baseFilters.qualification],
    });
  }, [filters, modes, filtersCleared, baseFilters]);

  const syncAppliedFilters = (
    nextFilters,
    nextModes = modes,
    nextCleared = filtersCleared,
    nextBase = baseFilters,
  ) => {
    setAppliedFilters(nextFilters);
    setAppliedModes({ ...nextModes });
    setAppliedFiltersCleared(nextCleared);
    setAppliedBaseFilters({
      department: [...(nextBase.department || [])],
      specialization: [...(nextBase.specialization || [])],
      qualification: [...(nextBase.qualification || [])],
    });
    setPage(1);
  };

  const commitFilters = (
    nextFilters,
    nextModes = modes,
    forceCleared,
    nextBase = baseFilters,
  ) => {
    const cleared =
      forceCleared !== undefined ? forceCleared : isFiltersEmpty(nextFilters);
    setFiltersCleared(cleared);
    syncAppliedFilters(cloneFilters(nextFilters), nextModes, cleared, nextBase);
  };

  const updateModes = (nextModes) => {
    // When a contains-capable filter's match type changes, re-derive its live
    // selection from the base values: Equals -> only base, Contains -> expanded.
    const nextFilters = cloneFilters(filters);
    CONTAINS_FILTER_KEYS.forEach((key) => {
      if (nextModes[key] !== modes[key]) {
        nextFilters[key] = deriveSelection(
          key,
          baseFilters[key],
          nextModes[key],
        );
      }
    });
    setModes(nextModes);
    setFilters(nextFilters);
    commitFilters(nextFilters, nextModes, undefined, baseFilters);
  };

  const handleApplySearch = () => {
    commitFilters(cloneFilters(filters), { ...modes });
  };

  const activeFilterCount = Object.values(filters).reduce(
    (n, arr) =>
      n + (Array.isArray(arr) ? arr.length : arr && arr !== "All" ? 1 : 0),
    0,
  );

  const removeFilter = (key, val) => {
    const nextFilters = {
      ...filters,
      [key]: Array.isArray(filters[key])
        ? filters[key].filter((v) => v !== val)
        : "",
    };
    // Keep the base in sync so a removed chip is not re-derived later.
    if (CONTAINS_FILTER_KEYS.includes(key) && Array.isArray(baseFilters[key])) {
      const nextBaseState = {
        ...baseFilters,
        [key]: baseFilters[key].filter((v) => v !== val),
      };
      setBaseFilters(nextBaseState);
      setFilters(nextFilters);
      commitFilters(nextFilters, modes, undefined, nextBaseState);
      return;
    }
    setFilters(nextFilters);
    commitFilters(nextFilters);
  };

  // Filters that support "Contains" match mode and auto-select related options.
  // Authority Name is excluded on purpose (Skills-style): only explicitly
  // selected values stay checked. Equals/Contains only changes match strictness.
  const CONTAINS_FILTER_KEYS = [
    "department",
    "specialization",
    "qualification",
  ];

  // Live options for a filter, falling back to mock data — mirrors exactly what
  // the FilterDropdown renders so similarity expansion stays in sync with the UI.
  const getOptionsForKey = (key) => {
    const optionsByKey = {
      department:
        departmentOptions.length > 0
          ? departmentOptions
          : mockFilterOptions.department || [],
      specialization:
        specializationOptions.length > 0
          ? specializationOptions
          : mockFilterOptions.specialization || [],
      qualification:
        qualificationOptions.length > 0
          ? qualificationOptions
          : mockFilterOptions.qualification || [],
      authorityName:
        authorityNameOptions.length > 0
          ? authorityNameOptions
          : mockFilterOptions.authorityName || [],
      skills:
        skillOptions.length > 0 ? skillOptions : mockFilterOptions.skills || [],
    };
    return optionsByKey[key] || [];
  };

  const getSearchOptionsByKey = () => ({
    city: cityOptions.length > 0 ? cityOptions : mockFilterOptions.city || [],
    profession:
      professionOptions.length > 0
        ? professionOptions
        : mockFilterOptions.profession || [],
    hospital:
      hospitalOptions.length > 0
        ? hospitalOptions
        : mockFilterOptions.hospital || [],
    status: mockFilterOptions.status || [],
    registration: mockFilterOptions.registration || [],
    department:
      departmentOptions.length > 0
        ? departmentOptions
        : mockFilterOptions.department || [],
    specialization:
      specializationOptions.length > 0
        ? specializationOptions
        : mockFilterOptions.specialization || [],
    qualification:
      qualificationOptions.length > 0
        ? qualificationOptions
        : mockFilterOptions.qualification || [],
    authorityName:
      authorityNameOptions.length > 0
        ? authorityNameOptions
        : mockFilterOptions.authorityName || [],
    skills:
      skillOptions.length > 0 ? skillOptions : mockFilterOptions.skills || [],
  });

  const makeEmptySearchDerivedFilters = () =>
    Object.fromEntries(SEARCHABLE_FILTER_KEYS.map((key) => [key, []]));

  const removeSearchDerivedSelections = (
    sourceFilters,
    sourceBaseFilters,
    derivedSelections,
  ) => {
    const nextFilters = cloneFilters(sourceFilters);
    const nextBase = { ...sourceBaseFilters };

    SEARCHABLE_FILTER_KEYS.forEach((key) => {
      const derivedValues = Array.isArray(derivedSelections[key])
        ? derivedSelections[key]
        : [];
      if (derivedValues.length === 0) return;

      if (Array.isArray(nextFilters[key])) {
        nextFilters[key] = nextFilters[key].filter(
          (v) => !derivedValues.includes(v),
        );
      }

      if (CONTAINS_FILTER_KEYS.includes(key) && Array.isArray(nextBase[key])) {
        nextBase[key] = nextBase[key].filter((v) => !derivedValues.includes(v));
      }
    });

    return { nextFilters, nextBase };
  };

  const resetSearchBarFilters = () => {
    const { nextFilters, nextBase } = removeSearchDerivedSelections(
      filters,
      baseFilters,
      searchDerivedFilters,
    );
    setBaseFilters(nextBase);
    setFilters(nextFilters);
    setSearchDerivedFilters(makeEmptySearchDerivedFilters());
    setAppliedSearch("");
    commitFilters(nextFilters, modes);
  };

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);

    if (value.trim() === "") {
      const hasSearchDerived = Object.values(searchDerivedFilters).some(
        (arr) => Array.isArray(arr) && arr.length > 0,
      );
      if (appliedSearch || hasSearchDerived) {
        resetSearchBarFilters();
      }
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const { fieldUpdates, unmatchedTokens } = resolveSearchQuery(
      searchInput,
      getSearchOptionsByKey(),
    );

    // Keep comma-separated free-text tokens intact so backend/global search
    // can interpret multi-keyword inputs like "rahul,9876543210".
    const textSearch =
      unmatchedTokens.length > 1
        ? unmatchedTokens.join(",")
        : (unmatchedTokens[0] || "").trim();
    setAppliedSearch(textSearch);

    const { nextFilters: filtersWithoutSearchDerived, nextBase: baseWithout } =
      removeSearchDerivedSelections(filters, baseFilters, searchDerivedFilters);

    const hasPreviousSearchDerived = Object.values(searchDerivedFilters).some(
      (arr) => Array.isArray(arr) && arr.length > 0,
    );

    if (Object.keys(fieldUpdates).length === 0) {
      setSearchDerivedFilters(makeEmptySearchDerivedFilters());
      if (hasPreviousSearchDerived) {
        setBaseFilters(baseWithout);
        setFilters(filtersWithoutSearchDerived);
        commitFilters(
          filtersWithoutSearchDerived,
          modes,
          undefined,
          baseWithout,
        );
      } else {
        setPage(1);
      }
      return;
    }

    // Search-bar mapping: single value per field stays strict (equals);
    // multiple values for the same field use contains (OR-style match).
    const nextModesForSearch = { ...modes };
    let nextFilters = cloneFilters(filtersWithoutSearchDerived);
    const nextBase = { ...baseWithout };

    Object.entries(fieldUpdates).forEach(([key, values]) => {
      if (CONTAINS_FILTER_KEYS.includes(key)) {
        nextBase[key] = Array.from(
          new Set([...(nextBase[key] || []), ...values]),
        );
      } else {
        nextFilters[key] = Array.from(
          new Set([...(nextFilters[key] || []), ...values]),
        );
      }
    });

    Object.keys(fieldUpdates).forEach((key) => {
      if (CONTAINS_FILTER_KEYS.includes(key)) {
        const mergedValues = nextBase[key] || [];
        nextModesForSearch[key] =
          mergedValues.length > 1 ? "contains" : "equals";
        nextFilters[key] = deriveSelection(
          key,
          mergedValues,
          nextModesForSearch[key],
        );
      }
    });

    const nextSearchDerived = makeEmptySearchDerivedFilters();
    Object.entries(fieldUpdates).forEach(([key, values]) => {
      nextSearchDerived[key] = Array.isArray(values) ? [...values] : [];
    });

    setBaseFilters(nextBase);
    setFilters(nextFilters);
    setSearchDerivedFilters(nextSearchDerived);
    setModes(nextModesForSearch);
    commitFilters(nextFilters, nextModesForSearch, undefined, nextBase);
  };

  // Every option similar to `val` (exact/contains/prefix/suffix/word/acronym),
  // always including `val` itself.
  const getContainsMatches = (key, val) =>
    findSimilarOptions(val, getOptionsForKey(key));

  // Derive the live selection for a contains-capable filter from its base
  // values: Equals -> base only; Contains -> base expanded by similarity.
  const deriveSelection = (key, baseArr, mode) => {
    if (!Array.isArray(baseArr)) return baseArr;
    if (mode !== "contains") return [...baseArr];
    const out = new Set();
    baseArr.forEach((val) =>
      getContainsMatches(key, val).forEach((m) => out.add(m)),
    );
    return Array.from(out);
  };

  const toggleFilter = (key, val) => {
    // Single-select (non-array) keys: unchanged.
    if (!Array.isArray(filters[key])) {
      const nextFilters = {
        ...filters,
        [key]: filters[key] === val ? "" : val,
      };
      setFilters(nextFilters);
      commitFilters(nextFilters);
      return;
    }

    // Contains-capable keys: the clicked option is a BASE value. Add/remove it
    // from the base, then re-derive the live selection from base + current mode.
    if (CONTAINS_FILTER_KEYS.includes(key)) {
      const isSelected = filters[key].includes(val);
      let nextBaseForKey;
      let nextSelection;
      if (isSelected) {
        // Deselect: drop from base and from the live selection.
        nextBaseForKey = baseFilters[key].filter((v) => v !== val);
        nextSelection = filters[key].filter((v) => v !== val);
      } else {
        // Select: add to base, regenerate the derived list per current mode.
        nextBaseForKey = baseFilters[key].includes(val)
          ? baseFilters[key]
          : [...baseFilters[key], val];
        nextSelection = deriveSelection(key, nextBaseForKey, modes[key]);
      }
      const nextFilters = { ...filters, [key]: nextSelection };
      const nextBaseState = { ...baseFilters, [key]: nextBaseForKey };
      setBaseFilters(nextBaseState);
      setFilters(nextFilters);
      commitFilters(nextFilters, modes, undefined, nextBaseState);
      return;
    }

    // Other multi-select keys: plain add/remove.
    const nextFilters = {
      ...filters,
      [key]: filters[key].includes(val)
        ? filters[key].filter((v) => v !== val)
        : [...filters[key], val],
    };
    setFilters(nextFilters);
    commitFilters(nextFilters);
  };

  const clearFilters = () => {
    const emptyFilters = {
      city: [],
      profession: [],
      department: [],
      hospital: [],
      status: [],
      registration: [],
      specialization: [],
      qualification: [],
      skills: [],
      experience: [],
      gender: [],
      bedSize: [],
      authorityName: [],
    };
    setFilters(emptyFilters);
    setBaseFilters({
      department: [],
      specialization: [],
      qualification: [],
    });
    clearRowSelection();
    commitFilters(emptyFilters, modes, true);
  };

  const [professionOptions, setProfessionOptions] = useState([]);
  const [specializationOptions, setSpecializationOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [qualificationOptions, setQualificationOptions] = useState([]);
  const [skillOptions, setSkillOptions] = useState([]);
  const [authorityNameOptions, setAuthorityNameOptions] = useState([]);
  const authorityNameOptionsRef = useRef([]);
  const [experienceOptions, setExperienceOptions] = useState([]);
  const [hospitalOptions, setHospitalOptions] = useState([]);
  const [hospitalBranches, setHospitalBranches] = useState([]);
  const [bedSizeOptions, setBedSizeOptions] = useState(BED_SIZE_OPTIONS);

  // Ensures the Jobs-page pre-selected values are expanded only once.
  const jobsExpandedRef = useRef(false);

  useEffect(() => {
    authorityNameOptionsRef.current = authorityNameOptions;
  }, [authorityNameOptions]);

  useEffect(() => {
    function handleDocumentClick() {
      setActiveDropdown(null);
    }
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setSelectedCandidateDetails(null);
        setIsInviteModalOpen(false);
      }
    };
    document.addEventListener("click", handleDocumentClick);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("click", handleDocumentClick);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [
          profRes,
          specRes,
          cityRes,
          deptRes,
          qualRes,
          skillsRes,
          expRes,
          hospRes,
          bedSizeRes,
        ] = await Promise.all([
          fetchProfessionType(),
          fetchSpecialisations(),
          fetchCityList(),
          fetchDepartments(),
          fetchQualificationType(),
          getSkillsList(false),
          fetchExperience(),
          hospitalBranch([true]),
          fetchBedSizeOptions(),
        ]);

        if (profRes?.response?.data?.docs) {
          setProfessionOptions(
            profRes.response.data.docs
              .map((item) => item?.name)
              .filter(Boolean),
          );
        }
        if (specRes?.response?.data?.docs) {
          setSpecializationOptions(
            specRes.response.data.docs
              .map((item) => item?.name)
              .filter(Boolean),
          );
        }
        if (cityRes?.response?.data?.docs) {
          setCityOptions(
            cityRes.response.data.docs
              .map((item) => item?.name)
              .filter(Boolean),
          );
        }
        if (deptRes?.response?.data?.docs) {
          setDepartmentOptions(
            deptRes.response.data.docs
              .map((item) => item?.name)
              .filter(Boolean),
          );
        }
        if (qualRes?.response?.data?.docs) {
          setQualificationOptions(
            qualRes.response.data.docs
              .map((item) => item?.name)
              .filter(Boolean),
          );
        }
        const skillDocs =
          skillsRes?.data?.docs ||
          skillsRes?.response?.data?.docs ||
          skillsRes?.docs ||
          [];
        if (skillDocs.length > 0) {
          setSkillOptions(skillDocs.map((item) => item?.name).filter(Boolean));
        }
        if (expRes?.response?.data?.docs) {
          setExperienceOptions(
            expRes.response.data.docs.map((item) => item?.name).filter(Boolean),
          );
        }

        const hospDocs =
          hospRes?.data?.docs ||
          hospRes?.response?.data?.docs ||
          hospRes?.docs ||
          [];
        if (hospDocs.length > 0) {
          setHospitalBranches(hospDocs);
          setHospitalOptions(
            hospDocs
              .map((item) => item?.name || item?.branchName)
              .filter(Boolean),
          );
        }

        if (Array.isArray(bedSizeRes) && bedSizeRes.length > 0) {
          setBedSizeOptions(bedSizeRes);
        }
      } catch (err) {
        console.error("Error fetching options", err);
      }
    };
    fetchOptions();
  }, []);

  // Jobs-page pre-selection: once the master data has loaded, run the same
  // similarity expansion on any value the Jobs page pre-selected, so arriving
  // from a job behaves identically to a manual selection in Contains mode.
  useEffect(() => {
    if (jobsExpandedRef.current || !incomingJob) return;

    // The *real* master data per key (no mock fallback). Expansion must wait
    // for these — running against mock data would match nothing and wrongly
    // mark the job as already expanded.
    const masterDataByKey = {
      department: departmentOptions,
      specialization: specializationOptions,
      qualification: qualificationOptions,
    };

    // Derive from the BASE selection (the values the Jobs page pre-populated),
    // so the result is identical to a manual selection in the current mode.
    const keysToExpand = CONTAINS_FILTER_KEYS.filter(
      (key) =>
        modes[key] === "contains" &&
        Array.isArray(baseFilters[key]) &&
        baseFilters[key].length > 0,
    );
    if (keysToExpand.length === 0) {
      jobsExpandedRef.current = true;
      return;
    }
    // Defer until every pre-selected contains-filter has its master data loaded.
    if (keysToExpand.some((key) => masterDataByKey[key].length === 0)) return;

    jobsExpandedRef.current = true;

    const nextFilters = { ...filters };
    let changed = false;
    keysToExpand.forEach((key) => {
      const derived = deriveSelection(key, baseFilters[key], modes[key]);
      if (derived.length !== filters[key].length) changed = true;
      nextFilters[key] = derived;
    });

    if (changed) {
      setFilters(nextFilters);
      commitFilters(nextFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    incomingJob,
    departmentOptions,
    specializationOptions,
    qualificationOptions,
  ]);

  const JOB = incomingJob
    ? {
        id: incomingJob.id || id || "LJ-2841",
        hospital: incomingJob.hospital || "Milann Hospital",
        branchId: incomingJob.branchId || incomingJob?.hospital?.id || "",
        city: incomingJob.city || "Bengaluru",
        profession: incomingJob.profession || "Doctor",
        department: incomingJob.department || "Gynaecology",
        specialization: incomingJob.specialization || "",
        qualification: incomingJob.qualification || "",
        status: incomingJob.status || "Open",
        date: incomingJob.date || "28 Apr 2026",
      }
    : {
        id: id || "LJ-2841",
        hospital: "Milann Hospital",
        branchId: "",
        city: "Bengaluru",
        profession: "Doctor",
        department: "Gynaecology",
        status: "Open",
        date: "28 Apr 2026",
      };

  const requiredJobProfession = (incomingJob?.profession || "").trim() || null;
  const requiredJobCity = (incomingJob?.city || "").trim() || null;

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setIsLoadingCandidates(true);

      const branchFilter = (() => {
        if (appliedFilters.hospital.length === 0) return undefined;

        if (hospitalBranches.length > 0) {
          const ids = hospitalBranches
            .filter((b) =>
              appliedFilters.hospital.includes(b.name || b.branchName),
            )
            .map((b) => b._id || b.id)
            .filter(Boolean);
          if (ids.length > 0) {
            return { ids, mode: appliedModes.hospital.toUpperCase() };
          }
        }

        if (!isUsersMode && JOB.branchId) {
          return {
            ids: [JOB.branchId],
            mode: appliedModes.hospital.toUpperCase(),
          };
        }

        return undefined;
      })();

      const payload = {
        ...(isUsersMode ? {} : { jobId: id }),
        search: appliedSearch,
        filters: {
          status:
            appliedFilters.status.length > 0
              ? appliedFilters.status
              : undefined,
          city:
            appliedFilters.city.length > 0 ? appliedFilters.city : undefined,
          profession:
            appliedFilters.profession.length > 0
              ? appliedFilters.profession
              : undefined,
          gender:
            appliedFilters.gender.length > 0
              ? appliedFilters.gender
              : undefined,
          registration:
            appliedFilters.registration.length > 0
              ? appliedFilters.registration
              : undefined,
          experience:
            appliedFilters.experience.length > 0
              ? appliedFilters.experience.map(normalizeExperienceValue)
              : undefined,
          bedSize:
            appliedFilters.bedSize.length > 0
              ? appliedFilters.bedSize
              : undefined,
          ...(branchFilter ? { branch: branchFilter } : {}),
          department: buildContainsMatchFilter(
            "department",
            appliedFilters,
            appliedBaseFilters,
            appliedModes,
          ),
          specialization: buildContainsMatchFilter(
            "specialization",
            appliedFilters,
            appliedBaseFilters,
            appliedModes,
          ),
          qualification: buildContainsMatchFilter(
            "qualification",
            appliedFilters,
            appliedBaseFilters,
            appliedModes,
          ),
          authorityName: (() => {
            if (!appliedFilters.authorityName?.length) return undefined;
            const mode = appliedModes.authorityName || "contains";
            const selected = appliedFilters.authorityName;
            // Use ref so contains expansion does not put options into fetch deps.
            const optionPool =
              authorityNameOptionsRef.current.length > 0
                ? authorityNameOptionsRef.current
                : selected;
            const values =
              mode === "contains"
                ? Array.from(
                    new Set(
                      selected.flatMap((name) =>
                        findSimilarOptions(name, optionPool),
                      ),
                    ),
                  )
                : selected;
            return {
              value: values.join(","),
              match: mode,
            };
          })(),
          skills: (() => {
            if (!appliedFilters.skills.length) return undefined;
            const mode = appliedModes.skills || "contains";
            const values =
              mode === "contains"
                ? expandSkillFilterValues(appliedFilters.skills, skillOptions)
                : appliedFilters.skills;
            return {
              value: values.join(","),
              match: mode,
            };
          })(),
        },
        sort: {
          field:
            sortOrder === "Most Shifts at this hospital"
              ? "mostShiftAtBranch"
              : sortOrder === "Most Recent Shift"
                ? "recentShift"
                : "updatedAt",
          order: "desc",
        },
        limit: rowsPerPage,
        offset: (page - 1) * rowsPerPage,
      };

      try {
        const res = await professionalSearch(payload);
        if (cancelled) return;

        if (res && res.docs) {
          const mapped = res.docs.map((doc, i) => {
            const skills = parseSkillsFromDoc(doc);
            const languages = parseLanguageFromDoc(doc);
            return {
              id: "CAN" + doc._id.substring(doc._id.length - 6).toUpperCase(),
              _id: doc._id,
              name: doc.name || "Unknown",
              profession: doc.profession,
              department: doc.departments?.[0] || "",
              deptExtra:
                doc.departments?.length > 1 ? doc.departments.length - 1 : null,
              specialization: doc.specializations?.[0] || "",
              departments: doc.departments || [],
              specializations: doc.specializations || [],
              qualification: doc.qualifications?.join(", ") || "",
              qualifications: doc.qualifications || [],
              skill: skills[0] || "",
              skills,
              skillsExtra: skills.length > 1 ? skills.length - 1 : null,
              experience:
                (Array.isArray(doc.experience) && doc.experience.join(", ")) ||
                doc.experience?.name ||
                doc.numExperience?.name ||
                doc.experience ||
                "",
              status: doc.status === "ACTIVE" ? "ACTIVE" : doc.status,
              city: doc.city,
              workedHere: doc.workedHereShiftCount,
              regStatus: doc.registrationValidity,
              regEndDate: doc.registrationEndDate
                ? new Date(doc.registrationEndDate).toLocaleDateString(
                    "en-GB",
                    {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    },
                  )
                : "",
              recentDate: doc.recentCompletedShiftDate
                ? new Date(doc.recentCompletedShiftDate).toLocaleDateString(
                    "en-GB",
                    { day: "2-digit", month: "short", year: "numeric" },
                  )
                : null,
              recent: null,
              updatedDate: doc.updatedAt
                ? new Date(doc.updatedAt).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })
                : null,
              gradient: defaultGradients[i % defaultGradients.length],
              phone: doc.phoneNumber || "",
              email: doc.email || doc.emails?.[0] || "",
              gender: doc.gender || "",
              languages,
              isInvited: doc.isInvited === true,
              isApplied: doc.isApplied === true,
              isAccepted: doc.isAccepted === true,
              inviteStatus: getInviteStatusLabel(doc),
              // invitedDate: formatCsvDate(Number(doc.invitedDate)),
              resumeUrl: doc.resumeUrl,
              authorityName: normalizeAuthorityName(
                doc.authorityName ||
                  doc.professionalRegistration?.instituteName,
              ),
              nameAsPerRegDoc: normalizeAuthorityName(
                doc.nameAsPerRegDoc ||
                  doc.professionalRegistration?.nameAsPerDocument,
              ),
              registrationCertificateUrl: getCertificateUrl(
                doc.registrationCertificate,
              ),
              verificationCertificateUrl: getCertificateUrl(
                doc.registrationVerificationCertificate ||
                  doc.verificationCertificate,
              ),
            };
          });
          const skillsFilterValues = appliedFilters.skills || [];
          const skillsFilterMode = appliedModes.skills || "contains";
          const authorityFilterValues = appliedFilters.authorityName || [];
          const authorityFilterMode = appliedModes.authorityName || "contains";
          let results = mapped;
          if (skillsFilterValues.length > 0) {
            results = results.filter((candidate) =>
              candidateMatchesSkillsFilter(
                candidate.skills,
                skillsFilterValues,
                skillsFilterMode,
              ),
            );
          }
          if (authorityFilterValues.length > 0) {
            results = results.filter((candidate) =>
              candidateMatchesAuthorityNameFilter(
                candidate.authorityName,
                authorityFilterValues,
                authorityFilterMode,
              ),
            );
          }
          const usedClientSideFilter =
            skillsFilterValues.length > 0 || authorityFilterValues.length > 0;
          setApiCandidates(results);
          setTotalCount(usedClientSideFilter ? results.length : res.total);
          // Never put authorityNameOptions in this effect's deps — updating
          // options from the response would re-trigger professionalSearch.
          setAuthorityNameOptions((prev) =>
            mergeAuthorityNameOptions(
              prev,
              mapped.map((candidate) => candidate.authorityName),
            ),
          );
        } else {
          setApiCandidates([]);
          setTotalCount(res?.total ?? 0);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Candidate search failed:", error);
          setApiCandidates([]);
          setTotalCount(0);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingCandidates(false);
        }
      }
    };
    fetchData();

    return () => {
      cancelled = true;
    };
  }, [
    JOB.id,
    JOB.branchId,
    hospitalBranches,
    appliedSearch,
    appliedFilters,
    appliedBaseFilters,
    appliedFiltersCleared,
    appliedModes,
    skillOptions,
    sortOrder,
    page,
    rowsPerPage,
  ]);

  const filtered = apiCandidates;
  const paginated = apiCandidates;
  const totalPages = Math.ceil(totalCount / rowsPerPage);
  const selectableCandidates = paginated.filter((c) =>
    canInviteCandidate(c, requiredJobProfession, requiredJobCity),
  );
  const hasNoData = !isLoadingCandidates && totalCount === 0;
  const isAllSelectableSelected =
    selectableCandidates.length > 0 &&
    selectableCandidates.every((c) => selectedRowIds.includes(c.id));

  const clearRowSelection = () => {
    setSelectedRowIds([]);
  };

  const toggleRowSelect = (candidate) => {
    if (
      !candidate ||
      !canInviteCandidate(candidate, requiredJobProfession, requiredJobCity)
    ) {
      return;
    }
    const id = candidate.id;
    setSelectedRowIds((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id],
    );
  };

  const toggleAllRows = () => {
    const selectableIds = selectableCandidates.map((c) => c.id);
    if (isAllSelectableSelected) {
      clearRowSelection();
    } else {
      setSelectedRowIds(selectableIds);
    }
  };

  useEffect(() => {
    setSelectedRowIds((prev) => {
      const validIds = new Set(
        apiCandidates
          .filter((c) =>
            canInviteCandidate(c, requiredJobProfession, requiredJobCity),
          )
          .map((c) => c.id),
      );
      const next = prev.filter((id) => validIds.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [apiCandidates, requiredJobProfession, requiredJobCity]);

  const handleExport = () => {
    const exportPermissionModule = isUsersMode ? "user" : "candidate_search";
    const hasExportPermission =
      adminData?.isSuperAdmin ||
      adminData?.permissions?.some(
        (item) =>
          item.module === exportPermissionModule &&
          item.actions.includes("export"),
      );

    if (hasExportPermission) {
      if (filtered.length === 0) {
        toast.error("No data to export");
        return;
      }

      setTimeout(() => {
        const headers = [
          "Name",
          "Phone",
          "Email",
          "Profession",
          "Department",
          "Specialisation",
          "Qualification",
          "Skills",
          "Languages",
          "Status",
          "Registration Status",
          "Registration End Date",
          "Authority Name",
          "Registration Certificate",
          "Verification Certificate",
          "City",
          "Gender",
          "Worked Here (Yes/No)",
          "Worked Here Count",
          "Recent Shift Date",
          "Profile Updated Date",
          ...(isUsersMode ? [] : ["Invite Status", "Invited Date"]),
        ];
        const rows = filtered.map((c) => [
          c.name || "",
          formatCsvPhone(c.phone),
          c.email || "",
          c.profession || "",
          c.departments?.length > 0
            ? c.departments.join(", ")
            : c.department || "",
          c.specializations?.length > 0
            ? c.specializations.join(", ")
            : c.specialization || "",
          c.qualifications?.length > 0
            ? c.qualifications.join(", ")
            : c.qualification || "",
          c.skills?.length > 0 ? c.skills.join(", ") : c.skill || "",
          c.languages || "",
          c.status || "",
          c.regStatus || "",
          c.regEndDate || "",
          c.authorityName || "",
          c.registrationCertificateUrl || "",
          c.verificationCertificateUrl || "",
          c.city || "",
          c.gender || "",
          c.workedHere ? "Yes" : "No",
          c.workedHere || 0,
          c.recentDate || "",
          c.updatedDate || "",
          ...(isUsersMode
            ? []
            : [c.inviteStatus || "Not Invited", c.invitedDate || ""]),
        ]);
        const csvContent = [
          headers.map(escapeCsvCell).join(","),
          ...rows.map((r) => r.map(escapeCsvCell).join(",")),
        ].join("\n");
        const blob = new Blob(["\uFEFF" + csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        const dateStr = new Date().toISOString().split("T")[0];
        link.setAttribute(
          "download",
          isUsersMode
            ? `Candidates_Advanced_Search_${dateStr}.csv`
            : `Candidates_${JOB.hospital.replace(/\s+/g, "_")}_${dateStr}.csv`,
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setIsExporting(false);
      }, 600);
    } else {
      toast.error("Access Denied: You don't have permission to export data");
    }
  };

  // Bulk filter updates (e.g. the dropdown's "Select all" / clear) bypass
  // toggleFilter, so mirror any changed contains-key into the base to keep the
  // base as the source of truth for later Equals <-> Contains toggles.
  const syncBaseWithFilters = (prev, updated) => {
    setBaseFilters((prevBase) => {
      let touched = false;
      const nextBase = { ...prevBase };
      CONTAINS_FILTER_KEYS.forEach((key) => {
        if (updated[key] !== prev[key]) {
          nextBase[key] = Array.isArray(updated[key]) ? [...updated[key]] : [];
          touched = true;
        }
      });
      return touched ? nextBase : prevBase;
    });
  };

  const updateFilters = (next) => {
    if (typeof next === "function") {
      setFilters((prev) => {
        const updated = next(prev);
        syncBaseWithFilters(prev, updated);
        commitFilters(updated);
        return updated;
      });
    } else {
      setFilters((prev) => {
        syncBaseWithFilters(prev, next);
        return next;
      });
      commitFilters(next);
    }
  };

  const dropdownProps = {
    activeDropdown,
    setActiveDropdown,
    filters,
    setFilters: updateFilters,
    removeFilter,
    toggleFilter,
  };

  const tableColSpan = isUsersMode ? 18 : 19;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <div className="cs-root">
        {/* Topbar equivalent */}
        <div className="cs-topbar cs-container">
          <button
            className="cs-back-btn"
            onClick={() => navigate(isUsersMode ? "/users" : -1)}
          >
            <I.ChevronLeft style={{ width: "16px", height: "16px" }} />{" "}
            <span>
              {isUsersMode
                ? "Back to Nurse/Hospital User"
                : "Back to Locum Jobs"}
            </span>
          </button>
        </div>

        <div className="cs-container">
          {/* Page Header */}
          <div className="cs-page-header">
            {isUsersMode ? (
              <>
                <div className="cs-header-content">
                  <div>
                    <h1 className="cs-title">Advanced Candidate Search</h1>
                  </div>
                </div>
                <div className="cs-nav-trail">
                  Users / <span>Advanced Candidate Search</span>
                </div>
              </>
            ) : (
              <>
                <div className="cs-nav-trail">
                  Locum Jobs / <span>{JOB.id}</span> / Candidate Search
                </div>
                <div className="cs-header-content">
                  <div>
                    <h1 className="cs-title">
                      Candidate Search & Job Matching
                    </h1>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Search Bar */}
          <form className="cs-search-form" onSubmit={handleSearchSubmit}>
            <label
              className={`cs-search-input-wrap ${searchInput ? "has-value" : ""}`}
            >
              <I.Search
                className="cs-search-icon"
                style={{ width: "16px", height: "16px" }}
              />
              <input
                value={searchInput}
                onChange={handleSearchInputChange}
                placeholder="Search by name, phone, city, profession, department, hospital, specialization, qualification, skills, status, registration, authority"
                className="cs-search-input"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchInput("");
                    resetSearchBarFilters();
                  }}
                  className="cs-chip-remove"
                  style={{
                    width: "14px",
                    height: "14px",
                    color: "rgba(11, 18, 32, 0.35)",
                  }}
                >
                  <I.X style={{ width: "14px", height: "14px" }} />
                </button>
              )}
            </label>
            <button type="submit" className="cs-btn-search">
              <I.Search style={{ width: "16px", height: "16px" }} /> Search
            </button>
          </form>

          {/* Advanced Filters */}
          <div className="cs-filters-panel">
            <button
              className="cs-filters-toggle"
              onClick={() => setShowFilters(!showFilters)}
            >
              <div className="cs-filters-toggle-left">
                <div className="cs-filter-icon-box">
                  <I.Filter style={{ width: "16px", height: "16px" }} />
                </div>
                <span className="cs-filters-title">Advanced filters</span>
                {activeFilterCount > 0 && (
                  <Badge tone="blue">{activeFilterCount} selected</Badge>
                )}
              </div>
              <div className="cs-filters-toggle-right">
                <span>{showFilters ? "Hide" : "Show"}</span>
                <I.Chevron
                  style={{
                    width: "16px",
                    height: "16px",
                    transform: showFilters ? "rotate(180deg)" : "none",
                    transition: "transform 0.15s",
                  }}
                />
              </div>
            </button>
            <div
              className="cs-filters-content"
              style={{
                maxHeight: showFilters ? "1200px" : "0px",
                overflow: showFilters ? "visible" : "hidden",
                transition: "max-height 0.3s ease-in-out",
              }}
            >
              <div className="cs-filters-body">
                <div className="cs-filters-grid">
                  <FilterDropdown
                    filterKey="city"
                    title="CITY"
                    dynamicOptions={
                      cityOptions.length > 0 ? cityOptions : undefined
                    }
                    {...dropdownProps}
                  />
                  <FilterDropdown
                    filterKey="profession"
                    title="PROFESSION"
                    dynamicOptions={
                      professionOptions.length > 0
                        ? professionOptions
                        : undefined
                    }
                    {...dropdownProps}
                  />
                  <FilterDropdown
                    filterKey="experience"
                    title="EXPERIENCE"
                    dynamicOptions={
                      experienceOptions.length > 0
                        ? experienceOptions
                        : undefined
                    }
                    {...dropdownProps}
                  />

                  <FilterDropdown
                    filterKey="department"
                    title="DEPARTMENT"
                    subtitle="other specialisation / skills"
                    dynamicOptions={
                      departmentOptions.length > 0
                        ? departmentOptions
                        : undefined
                    }
                    headerRight={
                      <div className="cs-filter-mode-toggle">
                        <button
                          className={`cs-mode-btn ${modes.department === "equals" ? "active" : ""}`}
                          onClick={() =>
                            updateModes({ ...modes, department: "equals" })
                          }
                        >
                          EQUALS
                        </button>
                        <button
                          className={`cs-mode-btn ${modes.department === "contains" ? "active" : ""}`}
                          onClick={() =>
                            updateModes({ ...modes, department: "contains" })
                          }
                        >
                          CONTAINS
                        </button>
                      </div>
                    }
                    {...dropdownProps}
                  />

                  <FilterDropdown
                    filterKey="hospital"
                    title="HOSPITAL"
                    dynamicOptions={
                      hospitalOptions.length > 0 ? hospitalOptions : undefined
                    }
                    headerRight={
                      <div className="cs-filter-mode-toggle">
                        <button
                          type="button"
                          className={`cs-mode-btn ${modes.hospital === "and" ? "active" : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            updateModes({ ...modes, hospital: "and" });
                          }}
                        >
                          AND
                        </button>
                        <button
                          type="button"
                          className={`cs-mode-btn ${modes.hospital === "or" ? "active" : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            updateModes({ ...modes, hospital: "or" });
                          }}
                        >
                          OR
                        </button>
                      </div>
                    }
                    {...dropdownProps}
                  />

                  <FilterDropdown
                    filterKey="specialization"
                    title="SPECIALIZATION"
                    dynamicOptions={
                      specializationOptions.length > 0
                        ? specializationOptions
                        : undefined
                    }
                    headerRight={
                      <div className="cs-filter-mode-toggle">
                        <button
                          className={`cs-mode-btn ${modes.specialization === "equals" ? "active" : ""}`}
                          onClick={() =>
                            updateModes({ ...modes, specialization: "equals" })
                          }
                        >
                          EQUALS
                        </button>
                        <button
                          className={`cs-mode-btn ${modes.specialization === "contains" ? "active" : ""}`}
                          onClick={() =>
                            updateModes({
                              ...modes,
                              specialization: "contains",
                            })
                          }
                        >
                          CONTAINS
                        </button>
                      </div>
                    }
                    {...dropdownProps}
                  />
                  <FilterDropdown
                    filterKey="qualification"
                    title="QUALIFICATION"
                    dynamicOptions={
                      qualificationOptions.length > 0
                        ? qualificationOptions
                        : undefined
                    }
                    headerRight={
                      <div className="cs-filter-mode-toggle">
                        <button
                          className={`cs-mode-btn ${modes.qualification === "equals" ? "active" : ""}`}
                          onClick={() =>
                            updateModes({ ...modes, qualification: "equals" })
                          }
                        >
                          EQUALS
                        </button>
                        <button
                          className={`cs-mode-btn ${modes.qualification === "contains" ? "active" : ""}`}
                          onClick={() =>
                            updateModes({ ...modes, qualification: "contains" })
                          }
                        >
                          CONTAINS
                        </button>
                      </div>
                    }
                    {...dropdownProps}
                  />
                  <FilterDropdown
                    filterKey="skills"
                    title="SKILLS"
                    dynamicOptions={
                      skillOptions.length > 0 ? skillOptions : undefined
                    }
                    headerRight={
                      <div className="cs-filter-mode-toggle">
                        <button
                          type="button"
                          className={`cs-mode-btn ${modes.skills === "equals" ? "active" : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            updateModes({ ...modes, skills: "equals" });
                          }}
                        >
                          EQUALS
                        </button>
                        <button
                          type="button"
                          className={`cs-mode-btn ${modes.skills === "contains" ? "active" : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            updateModes({ ...modes, skills: "contains" });
                          }}
                        >
                          CONTAINS
                        </button>
                      </div>
                    }
                    {...dropdownProps}
                  />
                  <FilterDropdown
                    filterKey="status"
                    title="STATUS"
                    {...dropdownProps}
                  />

                  <FilterDropdown
                    filterKey="registration"
                    title="REGISTRATION"
                    subtitle="reg. validity"
                    {...dropdownProps}
                  />
                  <FilterDropdown
                    filterKey="authorityName"
                    title="AUTHORITY NAME"
                    dynamicOptions={
                      authorityNameOptions.length > 0
                        ? authorityNameOptions
                        : undefined
                    }
                    headerRight={
                      <div className="cs-filter-mode-toggle">
                        <button
                          className={`cs-mode-btn ${modes.authorityName === "equals" ? "active" : ""}`}
                          onClick={() =>
                            updateModes({ ...modes, authorityName: "equals" })
                          }
                        >
                          EQUALS
                        </button>
                        <button
                          className={`cs-mode-btn ${modes.authorityName === "contains" ? "active" : ""}`}
                          onClick={() =>
                            updateModes({ ...modes, authorityName: "contains" })
                          }
                        >
                          CONTAINS
                        </button>
                      </div>
                    }
                    {...dropdownProps}
                  />
                  <FilterDropdown
                    filterKey="gender"
                    title="GENDER"
                    {...dropdownProps}
                  />
                  <FilterDropdown
                    filterKey="bedSize"
                    title="BED SIZE"
                    subtitle="hospital capacity"
                    dynamicOptions={bedSizeOptions}
                    {...dropdownProps}
                  />
                </div>

                <div className="cs-filters-footer">
                  <button className="cs-btn-clear" onClick={clearFilters}>
                    Clear All
                  </button>
                  <button
                    type="button"
                    className="cs-btn-apply"
                    onClick={handleApplySearch}
                  >
                    Apply Search
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Results Toolbar */}
          <div className="cs-toolbar">
            <div className="cs-toolbar-left">
              <div className="cs-matches-text">
                <strong>{totalCount}</strong> candidates matched
              </div>

              <div
                className="cs-sort-dropdown-wrap"
                style={{ position: "relative" }}
              >
                <button
                  className={`cs-sort-btn ${activeDropdown === "sort" ? "ACTIVE" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveDropdown(
                      activeDropdown === "sort" ? null : "sort",
                    );
                  }}
                >
                  <I.SortDesc
                    style={{
                      width: "16px",
                      height: "16px",
                      color: "rgba(11, 18, 32, 0.55)",
                    }}
                  />
                  <span className="cs-sort-label">Sort:</span>
                  {sortOrder === "Most Shifts at this hospital" && (
                    <I.Handshake style={{ width: "16px", height: "16px" }} />
                  )}
                  {sortOrder === "Most Recent Shift" && (
                    <I.Calendar style={{ width: "16px", height: "16px" }} />
                  )}
                  {sortOrder === "Profile Updated (latest)" && (
                    <I.Refresh style={{ width: "16px", height: "16px" }} />
                  )}
                  {sortOrder}
                  <I.Chevron
                    style={{
                      width: "16px",
                      height: "16px",
                      color: "rgba(11, 18, 32, 0.45)",
                    }}
                  />
                </button>
                {activeDropdown === "sort" && (
                  <div
                    className="cs-dropdown-menu sort-menu"
                    style={{ width: "240px" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="cs-dropdown-list">
                      {[
                        "Most Shifts at this hospital",
                        "Most Recent Shift",
                        "Profile Updated (latest)",
                      ].map((opt) => (
                        <button
                          key={opt}
                          className={`cs-dropdown-item ${sortOrder === opt ? "selected" : ""}`}
                          onClick={(e) => {
                            e.preventDefault();
                            setSortOrder(opt);
                            setActiveDropdown(null);
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            {opt === "Most Shifts at this hospital" && (
                              <I.Handshake
                                style={{ width: "14px", height: "14px" }}
                              />
                            )}
                            {opt === "Most Recent Shift" && (
                              <I.Calendar
                                style={{ width: "14px", height: "14px" }}
                              />
                            )}
                            {opt === "Profile Updated (latest)" && (
                              <I.Refresh
                                style={{ width: "14px", height: "14px" }}
                              />
                            )}
                            {opt}
                          </div>
                          {sortOrder === opt && (
                            <I.Check
                              style={{
                                width: "14px",
                                height: "14px",
                                color: "#4d8efd",
                              }}
                            />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <button
              className="cs-export-btn"
              onClick={handleExport}
              disabled={isExporting}
            >
              <I.Upload style={{ width: "16px", height: "16px" }} />{" "}
              {isExporting ? "Exporting..." : "Export Report"}
            </button>
          </div>

          {/* Table */}
          <div className="cs-table-wrap">
            <table className="cs-table">
              <thead>
                <tr>
                  {!isUsersMode && (
                    <th style={{ width: "40px" }}>
                      <div
                        className={`cs-table-checkbox ${selectableCandidates.length === 0 ? "disabled" : ""}`}
                        onClick={
                          selectableCandidates.length === 0
                            ? undefined
                            : toggleAllRows
                        }
                      >
                        <div
                          className={`cs-checkbox-box ${selectedRowIds.length > 0 && !isAllSelectableSelected ? "indeterminate" : isAllSelectableSelected ? "checked" : ""}`}
                        >
                          {selectedRowIds.length > 0 &&
                            !isAllSelectableSelected && (
                              <I.Minus
                                style={{
                                  width: "12px",
                                  height: "12px",
                                  color: "#fff",
                                }}
                              />
                            )}
                          {isAllSelectableSelected && (
                            <I.Check
                              style={{
                                width: "12px",
                                height: "12px",
                                color: "#fff",
                              }}
                            />
                          )}
                        </div>
                      </div>
                    </th>
                  )}
                  <th>NAME</th>
                  <th>PHONE</th>
                  <th>PROFESSION</th>
                  <th>EXPERIENCE</th>
                  <th>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span>DEPARTMENT</span>
                      <span
                        style={{
                          fontSize: "10px",
                          color: "rgba(11,18,32,0.4)",
                          textTransform: "none",
                          fontWeight: "500",
                        }}
                      >
                        other specialisation / skills
                      </span>
                    </div>
                  </th>
                  <th>SPECIALIZATION</th>
                  <th>QUALIFICATION</th>
                  <th>SKILLS</th>
                  <th>STATUS</th>
                  <th>CITY</th>
                  <th>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        color: "#4d8efd",
                      }}
                    >
                      <I.Handshake style={{ width: "14px", height: "14px" }} />{" "}
                      <span>Worked Here</span>
                    </div>
                  </th>
                  <th>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <I.Calendar style={{ width: "14px", height: "14px" }} />{" "}
                        <span>Recent Shift</span>
                      </div>
                    </div>
                  </th>
                  <th>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <I.Refresh style={{ width: "14px", height: "14px" }} />{" "}
                        <span>Profile Updated</span>
                      </div>
                    </div>
                  </th>
                  <th>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span>REGISTRATION</span>
                      <span
                        style={{
                          fontSize: "10px",
                          color: "rgba(11,18,32,0.4)",
                          textTransform: "none",
                          fontWeight: "500",
                        }}
                      >
                        end date
                      </span>
                    </div>
                  </th>
                  <th>AUTHORITY NAME</th>
                  <th>REG. CERTIFICATE</th>
                  <th>VERIFICATION CERT.</th>
                  <th style={{ textAlign: "right" }}>ACTION</th>
                </tr>
              </thead>
              <tbody>
                {isLoadingCandidates ? (
                  <tr>
                    <td
                      colSpan={tableColSpan}
                      className="cs-table-loading-cell"
                    >
                      <div className="cs-table-loader">
                        <I.Refresh className="cs-table-loader-icon" />
                        <span>Loading candidates...</span>
                      </div>
                    </td>
                  </tr>
                ) : paginated.length === 0 ? (
                  <tr>
                    <td colSpan={tableColSpan} className="cs-table-empty-cell">
                      <div className="cs-table-empty">
                        <div className="cs-table-empty-title">
                          No candidates found
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginated.map((c, i) => {
                    const isSelected = selectedRowIds.includes(c.id);
                    const inviteAllowed = canInviteCandidate(
                      c,
                      requiredJobProfession,
                      requiredJobCity,
                    );
                    const inactive = isCandidateInactive(c.status);
                    const professionMatches = candidateMatchesJobProfession(
                      c.profession,
                      requiredJobProfession,
                    );
                    const cityMatches = candidateMatchesJobCity(
                      c.city,
                      requiredJobCity,
                    );
                    return (
                      <tr
                        key={c.id}
                        className={isSelected ? "selected-row" : ""}
                      >
                        {!isUsersMode && (
                          <td style={{ verticalAlign: "middle" }}>
                            <div
                              className={`cs-table-checkbox ${!inviteAllowed ? "disabled" : ""}`}
                              onClick={() => toggleRowSelect(c)}
                            >
                              <div
                                className={`cs-checkbox-box ${isSelected ? "checked" : ""}`}
                              >
                                {isSelected && (
                                  <I.Check
                                    style={{
                                      width: "12px",
                                      height: "12px",
                                      color: "#fff",
                                    }}
                                  />
                                )}
                              </div>
                            </div>
                          </td>
                        )}
                        <td className="cs-clickable-cell">
                          <div className="cs-avatar-cell">
                            <Avatar name={c.name} gradient={c.gradient} />
                            <div>
                              <div className="cs-cand-name">{c.name}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ verticalAlign: "middle" }}>
                          {c.phone ? (
                            <span
                              className="cs-phone-link"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(`+${c.phone}`);
                                toast.success(
                                  "Phone number copied to clipboard.",
                                );
                              }}
                            >
                              +{c.phone}
                            </span>
                          ) : (
                            <span style={{ color: "rgba(11, 18, 32, 0.35)" }}>
                              —
                            </span>
                          )}
                        </td>
                        <td
                          style={{ verticalAlign: "middle", color: "#0B1220" }}
                          className="cs-clickable-cell"
                        >
                          {c.profession}
                        </td>
                        <td
                          style={{ verticalAlign: "middle", color: "#0B1220" }}
                          className="cs-clickable-cell"
                        >
                          {c.experience || "—"}
                        </td>
                        <td
                          style={{ verticalAlign: "middle" }}
                          className="cs-clickable-cell"
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              flexWrap: "wrap",
                            }}
                          >
                            <span
                              style={{ fontWeight: "600", color: "#0B1220" }}
                            >
                              {c.department}
                            </span>
                            {c.deptExtra > 0 && (
                              <span
                                className="cs-dept-tag"
                                style={{ margin: 0 }}
                                title={c.departments.join(", ")}
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                              >
                                +{c.deptExtra}
                              </span>
                            )}
                          </div>
                        </td>
                        <td
                          style={{ verticalAlign: "middle" }}
                          className="cs-clickable-cell"
                        >
                          <div style={{ color: "#0B1220" }}>
                            {c.specialization}
                          </div>
                        </td>
                        <td
                          style={{ verticalAlign: "middle" }}
                          className="cs-clickable-cell"
                        >
                          <div style={{ color: "#0B1220" }}>
                            {c.qualification}
                          </div>
                        </td>
                        <td
                          style={{ verticalAlign: "middle" }}
                          className="cs-clickable-cell"
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              flexWrap: "wrap",
                            }}
                          >
                            <span
                              style={{ fontWeight: "600", color: "#0B1220" }}
                            >
                              {c.skill || "—"}
                            </span>
                            {c.skillsExtra > 0 && (
                              <span
                                className="cs-dept-tag"
                                style={{ margin: 0 }}
                                title={c.skills.join(", ")}
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                              >
                                +{c.skillsExtra}
                              </span>
                            )}
                          </div>
                        </td>
                        <td style={{ verticalAlign: "middle" }}>
                          <StatusBadge value={c.status} />
                        </td>
                        <td
                          style={{ verticalAlign: "middle" }}
                          className="cs-clickable-cell"
                        >
                          <div style={{ color: "rgba(11, 18, 32, 0.7)" }}>
                            {c.city}
                          </div>
                        </td>
                        <td
                          style={{ verticalAlign: "middle" }}
                          className="cs-clickable-cell"
                        >
                          <WorkedBadge count={c.workedHere} />
                        </td>
                        <td
                          style={{ verticalAlign: "middle" }}
                          className="cs-clickable-cell"
                        >
                          {c.recentDate ? (
                            <div style={{ lineHeight: "1.4" }}>
                              <div
                                style={{ fontWeight: "500", color: "#0B1220" }}
                              >
                                {c.recentDate}
                              </div>
                              <div
                                style={{
                                  fontSize: "12px",
                                  color: "rgba(11, 18, 32, 0.45)",
                                }}
                              >
                                {c.recent}
                              </div>
                            </div>
                          ) : (
                            <span style={{ color: "rgba(11, 18, 32, 0.35)" }}>
                              —
                            </span>
                          )}
                        </td>
                        <td
                          style={{ verticalAlign: "middle" }}
                          className="cs-clickable-cell"
                        >
                          <div style={{ color: "rgba(11, 18, 32, 0.7)" }}>
                            {c.updatedDate}
                          </div>
                        </td>
                        <td style={{ verticalAlign: "middle" }}>
                          <RegBadge
                            status={c.regStatus}
                            endDate={c.regEndDate}
                          />
                        </td>
                        <td
                          style={{ verticalAlign: "middle", color: "#0B1220" }}
                          className="cs-clickable-cell"
                        >
                          {c.authorityName || (
                            <span style={{ color: "rgba(11, 18, 32, 0.35)" }}>
                              —
                            </span>
                          )}
                        </td>
                        <td style={{ verticalAlign: "middle" }}>
                          {c.registrationCertificateUrl ? (
                            <button
                              className="cs-btn-resume"
                              onClick={(e) => {
                                e.stopPropagation();
                                openResumeFile(c.registrationCertificateUrl);
                              }}
                            >
                              <I.Document
                                style={{ width: "14px", height: "14px" }}
                              />{" "}
                              View
                            </button>
                          ) : (
                            <span style={{ color: "rgba(11, 18, 32, 0.35)" }}>
                              —
                            </span>
                          )}
                        </td>
                        <td style={{ verticalAlign: "middle" }}>
                          {c.verificationCertificateUrl ? (
                            <button
                              className="cs-btn-resume"
                              onClick={(e) => {
                                e.stopPropagation();
                                openResumeFile(c.verificationCertificateUrl);
                              }}
                            >
                              <I.Document
                                style={{ width: "14px", height: "14px" }}
                              />{" "}
                              View
                            </button>
                          ) : (
                            <span style={{ color: "rgba(11, 18, 32, 0.35)" }}>
                              —
                            </span>
                          )}
                        </td>
                        <td
                          style={{
                            verticalAlign: "middle",
                            textAlign: "right",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "flex-end",
                              gap: "8px",
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              className="cs-btn-resume"
                              disabled={!c.resumeUrl}
                              onClick={() => openResumeFile(c.resumeUrl)}
                              style={{
                                opacity: c.resumeUrl ? 1 : 0.5,
                                cursor: c.resumeUrl ? "pointer" : "not-allowed",
                              }}
                            >
                              <I.Document
                                style={{ width: "14px", height: "14px" }}
                              />{" "}
                              Resume
                            </button>
                            {!isUsersMode &&
                              (c.inviteStatus === "Invited" ||
                              c.inviteStatus === "Applied" ||
                              c.inviteStatus === "Accepted" ? (
                                <button
                                  className="cs-btn-invite"
                                  style={{
                                    background:
                                      c.inviteStatus === "Applied"
                                        ? "#dbeafe"
                                        : c.inviteStatus === "Accepted"
                                          ? "#ede9fe"
                                          : "#d1fae5",
                                    color:
                                      c.inviteStatus === "Applied"
                                        ? "#1d4ed8"
                                        : c.inviteStatus === "Accepted"
                                          ? "#5b21b6"
                                          : "#065f46",
                                    borderColor:
                                      c.inviteStatus === "Applied"
                                        ? "#dbeafe"
                                        : c.inviteStatus === "Accepted"
                                          ? "#ede9fe"
                                          : "#d1fae5",
                                    cursor: "default",
                                  }}
                                >
                                  <I.Check
                                    style={{ width: "14px", height: "14px" }}
                                  />{" "}
                                  {c.inviteStatus}
                                </button>
                              ) : (
                                <button
                                  className="cs-btn-invite"
                                  disabled={!inviteAllowed}
                                  title={
                                    inactive
                                      ? "Inactive professionals cannot be invited"
                                      : !professionMatches &&
                                          requiredJobProfession
                                        ? `Only ${requiredJobProfession} candidates can be invited to this job`
                                        : !cityMatches && requiredJobCity
                                          ? isNcrCity(requiredJobCity)
                                            ? "Only professionals from NCR cities can be invited to this job"
                                            : `Only professionals from ${requiredJobCity} can be invited to this job`
                                          : undefined
                                  }
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (!inviteAllowed) return;
                                    setSelectedRowIds([c.id]);
                                    const hasAccess =
                                      adminData?.role === "Super_Admin" ||
                                      adminData?.permissions?.some(
                                        (perm) =>
                                          perm.module === "candidate_search" &&
                                          perm.actions?.includes("edit"),
                                      );

                                    if (!hasAccess) {
                                      return toast.error(
                                        "You do not have access to invite professionals.",
                                      );
                                    }

                                    setIsInviteModalOpen(true);
                                  }}
                                  style={{
                                    opacity: inviteAllowed ? 1 : 0.5,
                                    cursor: inviteAllowed
                                      ? "pointer"
                                      : "not-allowed",
                                  }}
                                >
                                  <I.Send
                                    style={{ width: "14px", height: "14px" }}
                                  />{" "}
                                  Invite
                                </button>
                              ))}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="cs-pagination-wrap">
            <div className="cs-page-size">
              Rows per page:
              <div className="cs-select-icon-wrap">
                <select
                  value={rowsPerPage}
                  disabled={hasNoData}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <I.Chevron
                  className="cs-select-chevron"
                  style={{ width: "12px", height: "12px" }}
                />
              </div>
            </div>
            <div className="cs-page-controls">
              <div className="cs-page-info">
                <strong>
                  {hasNoData ? 0 : (page - 1) * rowsPerPage + 1}–
                  {hasNoData
                    ? 0
                    : Math.min(page * rowsPerPage, totalCount || 1)}
                </strong>{" "}
                of <strong>{totalCount}</strong>
              </div>
              <div className="cs-page-btns">
                <button
                  className="cs-btn-page"
                  disabled={hasNoData || page === 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <I.ChevronLeft style={{ width: "16px", height: "16px" }} />{" "}
                  Prev
                </button>
                <button
                  className="cs-btn-page"
                  disabled={hasNoData || page * rowsPerPage >= totalCount}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next{" "}
                  <I.Chevron
                    style={{
                      width: "16px",
                      height: "16px",
                      transform: "rotate(-90deg)",
                    }}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Action Bar */}
      {!isUsersMode && (
        <div
          className={`cs-floating-action-bar ${selectedRowIds.length > 0 ? "visible" : ""}`}
        >
          <div className="cs-fab-content">
            <div className="cs-fab-count">
              <div className="cs-fab-badge">{selectedRowIds.length}</div>
              <span>selected</span>
            </div>
            <button
              type="button"
              className="cs-fab-clear"
              onClick={(e) => {
                e.stopPropagation();
                clearRowSelection();
              }}
            >
              Clear
            </button>
            <button
              className="cs-fab-invite"
              onClick={() => setIsInviteModalOpen(true)}
            >
              <I.Send style={{ width: "14px", height: "14px" }} /> Invite
              Selected
            </button>
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {!isUsersMode &&
        isInviteModalOpen &&
        (() => {
          const selectedCands = selectedRowIds
            .map((id) => apiCandidates.find((x) => x.id === id))
            .filter(Boolean);
          const skippedCands = selectedCands.filter(
            (c) =>
              !canInviteCandidate(c, requiredJobProfession, requiredJobCity),
          );
          const invitedCount = selectedCands.length - skippedCands.length;
          const skippedCount = skippedCands.length;

          return (
            <div
              className="cs-modal-overlay"
              onClick={() => setIsInviteModalOpen(false)}
            >
              <div className="cs-modal" onClick={(e) => e.stopPropagation()}>
                <div className="cs-modal-header">
                  <div className="cs-modal-icon-wrap">
                    <I.Send
                      style={{
                        width: "18px",
                        height: "18px",
                        color: "#059669",
                        transform: "rotate(-45deg)",
                        marginLeft: "3px",
                        marginBottom: "2px",
                      }}
                    />
                  </div>
                  <div>
                    <h3 className="cs-modal-title">
                      Invite {selectedRowIds.length} candidate
                      {selectedRowIds.length !== 1 ? "s" : ""}?
                    </h3>
                    <p className="cs-modal-subtitle">
                      to {JOB.hospital} · {JOB.department}, {JOB.date}
                    </p>
                  </div>
                  <button
                    className="cs-modal-close"
                    onClick={() => setIsInviteModalOpen(false)}
                  >
                    <I.X style={{ width: "18px", height: "18px" }} />
                  </button>
                </div>

                <div
                  className="cs-modal-body"
                  style={{ maxHeight: "400px", overflowY: "auto" }}
                >
                  {skippedCount > 0 && (
                    <div
                      style={{
                        background: "#fffbeb",
                        border: "1px solid #fde047",
                        color: "#b45309",
                        padding: "12px 16px",
                        borderRadius: "8px",
                        fontSize: "13.5px",
                        marginBottom: "16px",
                        lineHeight: "1.5",
                      }}
                    >
                      {invitedCount} will be invited · {skippedCount} will be
                      skipped (already invited / inactive / profession
                      mismatch / city mismatch).
                    </div>
                  )}

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    {selectedCands.map((c) => {
                      const isSkipped = !canInviteCandidate(
                        c,
                        requiredJobProfession,
                        requiredJobCity,
                      );
                      const skipReason =
                        c.inviteStatus === "Invited" ||
                        c.inviteStatus === "Applied" ||
                        c.inviteStatus === "Accepted"
                          ? c.inviteStatus.toLowerCase()
                          : isCandidateInactive(c.status)
                            ? "inactive"
                            : !candidateMatchesJobProfession(
                                  c.profession,
                                  requiredJobProfession,
                                )
                              ? "profession mismatch"
                              : "city mismatch";
                      return (
                        <div
                          key={c.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "16px",
                            paddingBottom: "12px",
                            borderBottom: "1px solid #f1f5f9",
                          }}
                        >
                          <Avatar
                            name={c.name}
                            gradient={c.gradient}
                            size={40}
                          />
                          <div style={{ flex: 1 }}>
                            <div
                              className="cs-modal-cand-name"
                              style={{ fontSize: "15px" }}
                            >
                              {c.name}
                            </div>
                            <div className="cs-modal-cand-id">
                              {c.profession}
                            </div>
                          </div>
                          <div>
                            {isSkipped ? (
                              <span
                                className="cs-badge"
                                style={{
                                  background: "#fef3c7",
                                  color: "#92400e",
                                  fontWeight: 600,
                                  padding: "4px 10px",
                                  fontSize: "12px",
                                }}
                              >
                                skip · {skipReason}
                              </span>
                            ) : (
                              <span
                                className="cs-badge"
                                style={{
                                  background: "#d1fae5",
                                  color: "#065f46",
                                  fontWeight: 600,
                                  padding: "4px 10px",
                                  fontSize: "12px",
                                }}
                              >
                                <I.Check
                                  style={{ width: "12px", height: "12px" }}
                                />{" "}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="cs-modal-footer">
                  <button
                    className="cs-btn-cancel"
                    onClick={() => setIsInviteModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="cs-btn-confirm"
                    disabled={invitedCount === 0 || isInviting}
                    style={{
                      opacity: invitedCount === 0 || isInviting ? 0.5 : 1,
                      cursor:
                        invitedCount === 0 || isInviting
                          ? "not-allowed"
                          : "pointer",
                    }}
                    onClick={async () => {
                      console.log("here in invite onclick", adminData);
                      const hasAccess =
                        adminData?.role === "Super_Admin" ||
                        adminData?.permissions?.some(
                          (perm) =>
                            perm.module === "candidate_search" &&
                            perm.actions?.includes("edit"),
                        );

                      if (!hasAccess) {
                        return toast.error(
                          "You do not have access to invite professionals.",
                        );
                      }

                      if (invitedCount === 0 || isInviting) return;
                      setIsInviting(true);

                      const professionalIds = selectedCands
                        .filter((c) =>
                          canInviteCandidate(
                            c,
                            requiredJobProfession,
                            requiredJobCity,
                          ),
                        )
                        .map((c) => c._id || c.id);

                      const payload = {
                        jobId: id,
                        professionalIds: professionalIds,
                      };

                      try {
                        const response = await invitedJobsService(payload);
                        setIsInviting(false);

                        if (response && response.success) {
                          setIsInviteModalOpen(false);

                          // Immediately update the local state so the button turns to "Invited" without a refresh
                          setApiCandidates((prev) =>
                            prev.map((c) =>
                              professionalIds.includes(c._id || c.id)
                                ? {
                                    ...c,
                                    inviteStatus: "Invited",
                                    isInvited: true,
                                  }
                                : c,
                            ),
                          );

                          clearRowSelection();
                          toast.success(
                            `${invitedCount} invited: All notified for ${JOB.hospital} · ${JOB.date}.`,
                          );
                        } else {
                          toast.error(
                            response?.error ||
                              response?.msg ||
                              "Failed to invite candidates",
                          );
                        }
                      } catch (error) {
                        setIsInviting(false);
                        toast.error(
                          error?.response?.data?.msg ||
                            error?.response?.data?.message ||
                            error?.message ||
                            "An unexpected error occurred",
                        );
                      }
                    }}
                  >
                    <I.Send style={{ width: "14px", height: "14px" }} />{" "}
                    {isInviting
                      ? "Sending..."
                      : `Send ${invitedCount} Invite${invitedCount !== 1 ? "s" : ""}`}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

      {/* Candidate Details Modal temporarily disabled */}
    </DashboardLayout>
  );
}
